/**
 * Phase D release hub — the REAL shell (Task D3). Replaces D1's placeholder
 * body in place; this is still the ONLY file registering the `/releases`
 * route (see App.tsx) — do not add a second one.
 *
 * Tabs follow the platform-wide pill-tab standard (emerald active pill,
 * icon + label, inside the black header) — see
 * feedback_tab_style_standard.md. The page CHROME (black header, ruby
 * gradient underline, light stone content area) is the RUBY theme standard
 * from StaffChrome.tsx — never purple.
 */
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layers, LayoutGrid, PackageSearch, Rocket } from 'lucide-react';
import { StaffHeader } from '../../components/staff/StaffChrome';
import AppsTab from './AppsTab';
import BuildsTab from './BuildsTab';
import RolloutTab from './RolloutTab';

type TabId = 'apps' | 'builds' | 'rollout';

const TABS: { id: TabId; label: string; icon: React.FC<any> }[] = [
  { id: 'apps', label: 'Apps', icon: LayoutGrid },
  { id: 'builds', label: 'Builds', icon: PackageSearch },
  { id: 'rollout', label: 'Rollout', icon: Rocket },
];
const VALID_TABS = TABS.map((t) => t.id);

/** Emerald pill sub-tabs per feedback_tab_style_standard.md — dark variant, inside the black header. */
const EmeraldPillTabs: React.FC<{ tabs: typeof TABS; active: TabId; onChange: (id: TabId) => void }> = ({ tabs, active, onChange }) => (
  <div className="flex gap-2 flex-wrap">
    {tabs.map((t) => {
      const Icon = t.icon;
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors min-h-[44px] ${
            isActive
              ? 'bg-emerald-600 border-emerald-600 text-white'
              : 'border-white/30 text-gray-400 hover:text-white hover:border-white/50'
          }`}
        >
          <Icon size={14} />
          {t.label}
        </button>
      );
    })}
  </div>
);

function ReleasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const tab: TabId = fromUrl && (VALID_TABS as string[]).includes(fromUrl) ? (fromUrl as TabId) : 'apps';

  const setTab = useCallback((next: TabId) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    }, { replace: false });
  }, [setSearchParams]);

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      <StaffHeader
        icon={Layers}
        section="Deployment & Release"
        title="Releases"
        subtitle="Which build every device is running, and where it drifts from the newest one."
        tabs={<EmeraldPillTabs tabs={TABS} active={tab} onChange={setTab} />}
      />
      <div className="bg-stone-100 min-h-[calc(100vh-72px)] text-stone-800">
        <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px] mx-auto">
          {tab === 'apps' && <AppsTab />}
          {tab === 'builds' && <BuildsTab />}
          {tab === 'rollout' && <RolloutTab />}
        </div>
      </div>
    </div>
  );
}

export default ReleasesPage;
